<template>
    <div v-if="loaded">
        <rkm-area
            no-margin-top
            title="Filtros"
            type="fieldset"
        >
            <rkm-form
                ref="Form"
                show-legend
            >
                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-date-picker
                            :end-validations="validations.periodo"
                            :max-date="additionals.dataAtual"
                            :range-inline="false"
                            :start-validations="validations.periodo"
                            end-label="Até"
                            range
                            start-label="Período de"
                            v-model="model.periodo"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            :disabled="disableUnidadeReferencia"
                            :options="form.unidadesReferencia"
                            @change="loadFrom('unidadeReferencia')"
                            default-option-text="Todas"
                            label="Unidade de referência"
                            show-default-option
                            text-field="nomeFantasia"
                            v-model="model.unidadeReferenciaId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-select
                            :disabled="disableUnidadeSaida"
                            :options="form.unidadesSaida"
                            @change="loadFrom('unidadeSaida')"
                            default-option-text="Todas"
                            label="Unidade de saída"
                            show-default-option
                            text-field="nomeFantasia"
                            v-model="model.unidadeSaidaId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            :disabled="disableProfissionalResponsavel"
                            :options="form.profissionaisResponsaveis"
                            @change="loadFrom('profissionalResponsavel')"
                            default-option-text="Todos"
                            label="Profissional responsável"
                            show-default-option
                            text-field="nome"
                            v-model="model.profissionalResponsavelId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-select
                            :disabled="disableGrupo"
                            :options="form.grupos"
                            @change="loadFrom('grupo')"
                            default-option-text="Todos"
                            label="Grupo"
                            show-default-option
                            text-field="nome"
                            v-model="model.grupoId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            :disabled="disableSubgrupo"
                            :options="form.subgrupos"
                            @change="loadFrom('subgrupo')"
                            default-option-text="Todos"
                            label="Subgrupo"
                            show-default-option
                            text-field="nome"
                            v-model="model.subgrupoId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-input-autocomplete
                            :disabled="disableMaterial"
                            :repository="repositories.relatorioGeralSaidaPaciente.material"
                            :request-query-string-params="autocompleteQueryStringParams.material"
                            :search-fields="autocompleteFields.material"
                            text-field="descricao"
                            label="Material"
                            uppercase
                            v-model="model.material"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-input-autocomplete
                            :disabled="disablePaciente"
                            :repository="repositories.relatorioGeralSaidaPaciente.paciente"
                            :request-query-string-params="autocompleteQueryStringParams.paciente"
                            :search-fields="autocompleteFields.paciente"
                            text-field="nome"
                            label="Paciente"
                            uppercase
                            v-model="model.paciente"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="auto">
                        <rkm-button
                            :label="false"
                            @click="$refs.Form.validate(search)"
                            color="primary"
                        >
                            Buscar
                        </rkm-button>

                        <rkm-button
                            :label="false"
                            @click="reset"
                        >
                            Limpar
                        </rkm-button>
                    </rkm-col>
                </rkm-row>
            </rkm-form>
        </rkm-area>

        <rkm-area
            no-margin-top
            title="Resultado"
        >
            <template v-slot:trailing>
                <rkm-dropdown
                    class="mr-2"
                    color="primary"
                    cols="1"
                    menu-class="auto-scroll"
                    no-caret
                    small
                    title="Colunas"
                    v-rkm-tooltip.hover
                >
                    <template v-slot:button-content>
                        <rkm-icon icon="columns" />
                    </template>

                    <rkm-form-checkbox
                        :indeterminate="indeterminate"
                        @change="selectAll"
                        class="mx-2"
                        v-model="selectedAll"
                    >
                        Todas
                    </rkm-form-checkbox>

                    <rkm-form-checkbox-group
                        :options="optionalColumns"
                        class="mx-2"
                        stacked
                        text-field="label"
                        v-model="selectedCols"
                        value-field="key"
                    />
                </rkm-dropdown>

                <rkm-button
                    :disabled="itens.length === 0"
                    @click="print"
                    color="info"
                    small
                    title="Imprimir"
                >
                    <rkm-icon icon="print" />
                </rkm-button>

                <rkm-button
                    :disabled="itens.length === 0"
                    @click="exportTo"
                    color="info"
                    small
                    title="Exportar"
                >
                    <rkm-icon icon="file-export" />
                </rkm-button>
            </template>

            <rkm-table
                :fields="fields"
                :foot-clone="itens.length > 0"
                :items="itens"
                :sort-by="sortBy"
                :sort-desc="sortDesc"
                @sort-changed="onSortChanged"
                no-footer-sorting
                responsive
            >
                <template #cell(lote)="{ value }">
                    {{ value || '-' }}
                </template>

                <template #cell(validade)="{ value }">
                    {{ value || '-' }}
                </template>

                <template #cell(unidadeMedida)="{ value }">
                    {{ value || '-' }}
                </template>

                <template #cell(cpf)="{ value }">
                    {{ value || '-' }}
                </template>

                <template #cell(cns)="{ value }">
                    {{ value || '-' }}
                </template>

                <template #cell(dataNascimento)="{ value }">
                    {{ isNullOrEmptyString(value) ? '-' : formatDate(value) }}
                </template>

                <template #cell(endereco)="{ value }">
                    {{ value || '-' }}
                </template>

                <template #cell(unidadePaciente)="{ value }">
                    {{ value || '-' }}
                </template>

                <template #cell(equipePaciente)="{ value }">
                    {{ value || '-' }}
                </template>

                <template #cell(grupo)="{ value }">
                    {{ value || '-' }}
                </template>

                <template #cell(subgrupo)="{ value }">
                    {{ value || '-' }}
                </template>

                <template #foot(dataHora)>
                    <span></span>
                </template>

                <template #foot(hora)>
                    <span></span>
                </template>

                <template #foot(unidade)>
                    <span></span>
                </template>

                <template #foot(tipoAtendimento)>
                    <span></span>
                </template>

                <template #foot(paciente)>
                    <span></span>
                </template>

                <template #foot(prontuario)>
                    <span></span>
                </template>

                <template #foot(profissionalResponsavel)>
                    <span></span>
                </template>

                <template #foot(material)>
                    <span></span>
                </template>

                <template #foot(lote)>
                    <span></span>
                </template>

                <template #foot(validade)>
                    <span></span>
                </template>

                <template #foot(quantidade)>
                    <span></span>
                </template>

                <template #foot(unidadeMedida)>
                    <span></span>
                </template>

                <template #foot(valorTotal)>
                    <span></span>
                </template>

                <template #foot(cpf)>
                    <span></span>
                </template>

                <template #foot(cns)>
                    <span></span>
                </template>

                <template #foot(dataNascimento)>
                    <span></span>
                </template>

                <template #foot(endereco)>
                    <span></span>
                </template>

                <template #foot(unidadePaciente)>
                    <span></span>
                </template>

                <template #foot(equipePaciente)>
                    <span></span>
                </template>

                <template #foot(grupo)>
                    <span></span>
                </template>

                <template #foot(subgrupo)>
                    <span></span>
                </template>

                <template #afterTable>
                    <rkm-row class="mt-2">
                        <rkm-col class="text-center font-weight-bold">
                            <span>Total de pacientes: {{ getSafely(() => totalizadores.pacientes, 0) }}</span>
                            <span class="ml-4">Total de unidades: {{ getSafely(() => totalizadores.unidades, 0) }}</span>
                            <span class="ml-4">Total de materiais: {{ getSafely(() => totalizadores.materiais, 0) }}</span>
                            <span class="ml-4">Valor financeiro total: {{ formatMoney(getSafely(() => totalizadores.valorTotal, 0), 2) }}</span>
                        </rkm-col>
                    </rkm-row>
                </template>
            </rkm-table>
        </rkm-area>
    </div>
</template>

<script>
import formatsFilter from '@/filters/formats'
import { mapActions } from 'vuex'
import { repository, error, data, object, stringCase, file } from '@/mixins'

export default {
    computed: {
        autocompleteQueryStringParams() {

            const params = this.prepareData()

            return {
                material: { ...params, material_id: undefined },
                paciente: { ...params, paciente_id: undefined },
            }
        },
        autocompleteFields() {

            return {
                material: [{ key: 'descricao', searchable: true }],
                paciente: [{ key: 'nome', searchable: true }],
            }
        },
        optionalColumns() {

            return this.columns.filter(c => !(c.fixed || false))
        },
        fields() {

            return this.columns.filter(c => (c.fixed || false) || this.selectedCols.includes(c.key))
        },
        validations() {

            return {
                periodo: { required: true },
            }
        },
        disableUnidadeReferencia() {

            return !this.isPeriodoSelecionado
        },
        disableUnidadeSaida() {

            return !this.isPeriodoSelecionado
        },
        disableGrupo() {

            return !this.isPeriodoSelecionado
        },
        disableSubgrupo() {

            return !this.isPeriodoSelecionado
                || this.model.grupoId === null
        },
        disableMaterial() {

            return !this.isPeriodoSelecionado
        },
        disablePaciente() {

            return !this.isPeriodoSelecionado
        },
        disableProfissionalResponsavel() {

            return !this.isPeriodoSelecionado
        },
        isPeriodoSelecionado() {

            return this.model !== null
                && (this.model.periodo.start instanceof Date)
                && (this.model.periodo.end instanceof Date)
        },
    },
    created() {

        this.repositories = {
            relatorioGeralSaidaPaciente: this.getConectaSusRepository('relatorios.relatorioGeralSaidaPaciente'),
            impressao: this.getConectaSusRepository('impressao.relatorios'),
            exportacao: this.getConectaSusRepository('exportacao.relatorios'),
        }

        this.load()
    },
    data() {

        return {
            loaded: false,
            additionals: null,
            form: null,
            model: null,
            initials: {
                form: null,
                model: null,
            },
            selectedAll: false,
            indeterminate: true,
            selectedCols: [],
            sortBy: 'dataHora',
            sortDesc: false,
            columns: [
                {
                    key: 'dataHora',
                    label: 'Data',
                    sortable: true,
                    class: 'col-min text-center',
                    formatter: value => this.formatDate(value),
                    filterByFormatted: true,
                    fixed: true,
                },
                {
                    key: 'hora',
                    label: 'Hora',
                    sortable: true,
                    class: 'col-min text-center',
                    formatter: value => this.formatTime(value),
                    filterByFormatted: true,
                    fixed: true,
                },
                { key: 'unidade', label: 'Unidade', sortable: true, fixed: true },
                { key: 'tipoAtendimento', label: 'Tipo de atendimento', sortable: true, class: 'col-min text-center', fixed: true },
                { key: 'paciente', label: 'Paciente', sortable: true, fixed: true },
                { key: 'prontuario', label: 'Prontuário', sortable: true, class: 'col-min text-center', fixed: true },
                { key: 'profissionalResponsavel', label: 'Profissional responsável', sortable: true, fixed: true },
                { key: 'material', label: 'Material', sortable: true, fixed: true },
                { key: 'lote', label: 'Lote', sortable: true, class: 'col-min text-center', fixed: true },
                {
                    key: 'validade',
                    label: 'Validade',
                    sortable: true,
                    class: 'col-min text-center',
                    formatter: value => this.isNullOrEmptyString(value) ? '' : this.formatDate(value),
                    filterByFormatted: true,
                    fixed: true,
                },
                { key: 'quantidade', label: 'Quantidade', sortable: true, class: 'col-min text-center', fixed: true },
                { key: 'unidadeMedida', label: 'Unidade de medida', sortable: true, class: 'col-min text-center', fixed: true },
                {
                    key: 'valorTotal',
                    label: 'Valor total',
                    sortable: true,
                    class: 'col-min text-center',
                    formatter: value => this.formatMoney(Number.parseFloat(value), 2),
                    fixed: true,
                },
                { key: 'cpf', label: 'CPF', sortable: true, class: 'col-min text-center', formatter: value => this.isNullOrEmptyString(value) ? '' : this.formatCpf(value), filterByFormatted: true },
                { key: 'cns', label: 'CNS', sortable: true, class: 'col-min text-center', formatter: value => this.isNullOrEmptyString(value) ? '' : this.formatCns(value), filterByFormatted: true },
                {
                    key: 'dataNascimento',
                    label: 'Data de nascimento',
                    sortable: true,
                    class: 'col-min text-center',
                    formatter: value => this.isNullOrEmptyString(value) ? '' : this.formatDate(value),
                    filterByFormatted: true,
                },
                { key: 'endereco', label: 'Endereço', sortable: true },
                { key: 'unidadePaciente', label: 'Unidade vinculada do paciente', sortable: true },
                { key: 'equipePaciente', label: 'Equipe vinculada do paciente', sortable: true },
                { key: 'grupo', label: 'Grupo', sortable: true },
                { key: 'subgrupo', label: 'Subgrupo', sortable: true },
            ],
            itens: [],
            totalizadores: null,
        }
    },
    methods: {
        ...formatsFilter,
        ...mapActions(['setLoading', 'unsetLoading']),
        onSortChanged(context) {

            this.sortBy = context.sortBy
            this.sortDesc = context.sortDesc
        },
        load() {

            this.setLoading()

            this.repositories.relatorioGeralSaidaPaciente.list()
                .then(({ data }) => {

                    this.additionals = data.additionals
                    this.form = data.form
                    this.model = data.model

                    this.initials.form = this.clone(data.form)
                    this.initials.model = this.clone(data.model)

                    this.addWatchers()

                    this.loaded = true
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        addWatchers() {

            this.$watch('model.periodo', {
                deep: true,
                handler(periodo) {

                    if (periodo.start === null || periodo.end === null) {

                        this.resetAfterPeriodo()
                    }
                    else {

                        this.loadFrom('periodo')
                    }
                }
            })
        },
        loadFrom(field) {

            this.clearFrom(field)

            this.setLoading()

            this.repositories.relatorioGeralSaidaPaciente.list({ ...this.prepareData(), load_from: this.camelToSnake(field) })
                .then(({ data }) => {

                    if ('form' in data) {

                        Object.keys(data.form).forEach(key => this.$set(this.form, key, data.form[key]))
                    }

                    Object.keys(data.model).forEach(key => this.$set(this.model, key, data.model[key]))
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        clearFrom(field) {

            if (field == 'periodo') {

                return
            }

            if (!['material', 'paciente'].includes(field)) {

                field = `${field}Id`
            }

            const fields = [
                'unidadeReferenciaId',
                'unidadeSaidaId',
                'profissionalResponsavelId',
                'grupoId',
                'subgrupoId',
                'material',
                'paciente',
            ]

            fields.slice(fields.indexOf(field) + 1, fields.length)
                .forEach(key => this.$set(this.model, key, null))
        },
        search() {

            this.setLoading()

            const params = this.prepareData()
            params.selectedCols = this.columns.map(c => this.camelToSnake(c.key))

            this.repositories.relatorioGeralSaidaPaciente.index(params)
                .then(({ data }) => {

                    this.itens = data.itens
                    this.totalizadores = data.totalizadores
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        reset() {

            this.form = this.clone(this.initials.form)
            this.model = this.clone(this.initials.model)

            this.$nextTick().then(() => this.$refs.Form.resetValidator())
        },
        resetAfterPeriodo() {

            this.form = this.clone(this.initials.form)

            const model = this.clone(this.initials.model)

            delete model.periodo

            Object.keys(model).forEach(key => this.$set(this.model, key, model[key]))
        },
        print() {

            this.setLoading()

            this.repositories.impressao.relatorioGeralSaidaPaciente(this.prepareDataWithColumns())
                .then(({ data, headers }) => this.openFile(data, headers['content-type']))
                .finally(() => this.unsetLoading())
        },
        exportTo() {

            this.setLoading()

            this.repositories.exportacao.relatorioGeralSaidaPaciente(this.prepareDataWithColumns())
                .then(({ data, headers }) => this.openFile(data, headers['content-type']))
                .finally(() => this.unsetLoading())
        },
        prepareDataWithColumns() {

            const data = this.prepareData()

            data.sortBy = this.sortBy ? this.camelToSnake(this.sortBy) : null
            data.sortDesc = this.sortDesc

            data.selectedCols = this.fields.map(f => ({
                key: this.camelToSnake(f.key),
                label: f.label,
                class: f.class
            }))

            return data
        },
        prepareData() {

            const data = this.clone(this.model)

            this.formateDates(data.periodo, ['start', 'end'], 'DD/MM/YYYY')
            this.createIdKeysToObject(data, ['material', 'paciente'])

            return this.camelObjectKeysToSnakeRecursive(data)
        },
        selectAll(selected) {

            this.selectedCols = selected ? this.optionalColumns.map(c => c.key) : []
        },
    },
    mixins: [repository, error, data, object, stringCase, file],
    watch: {
        model: {
            deep: true,
            handler() {

                this.itens = []
                this.totalizadores = null
            }
        },
        selectedCols(newValue) {

            this.selectedAll = newValue.length > 0 && newValue.length == this.optionalColumns.length
            this.indeterminate = newValue.length > 0 && newValue.length != this.optionalColumns.length
        },
    }
}
</script>

<style scoped>
.dropdown >>> .auto-scroll {
    max-height: 400px;
    overflow-y: auto;
    width: 400px;
}
</style>
